import { Divider } from "@affinity/components/Divider";
import { Head } from "@affinity/components/Head";
import { AddressType } from "@affinity/constants/SharedTypes";
import { bodyFont, colours } from "@affinity/constants/StyleConstants";
import { rem } from "@affinity/constants/StyleHelpers";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { loadPageByType } from "@insite/client-framework/Store/Data/Pages/PagesActionCreators";
import { getHeader, getPageStateByType } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { Button, Select, TextField } from "@insite/mobius/index";
import React, { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { createGlobalStyle, css } from "styled-components";

export type AddressLookupProps = {
    onSubmit: (value: AddressType) => void;
} & ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

const GlobalStyle = createGlobalStyle`
body {
    .pca {
        .pcaautocomplete {
            margin-top: 2px;
            border-radius: 5px;
            border: 2px solid ${colours.darkBlue60};
            background: rgb(255, 255, 255);
            overflow: hidden;
            max-width: calc(100vw - 44px);
            .pcamessage {
                font-family: ${bodyFont} !important;
                font-weight: 300;
            }
            .pcaheader {
                background: ${colours.lightBlue};
                color: ${colours.darkBlue};
                display: none;
            }
            .pcalist {
                height: auto !important;
                max-height: 300px;
            }
            .pcalist, .pcafooter, .pcaitem {
                background: none;
                border: none;
                font-family: ${bodyFont};
                font-weight: 300;
                text-wrap: wrap;
            }
            .pcaitem {
                font-size: 14px;
                padding: 10px;
            }
            .pcaselected {
                background: ${colours.lightBlue20};
            }
        }
    }
}
`;

const mapStateToProps = (state: ApplicationState) => {
    const { value: settings, isLoading } = getPageStateByType(state, "AffinitySettings");
    return {
        addressnowKey: settings?.fields?.addressnowKey,
        isLoading,
    };
};

const mapDispatchToProps = {
    loadPageByType,
};

export const AddressLookup = connect(
    mapStateToProps,
    mapDispatchToProps,
)(({ onSubmit, loadPageByType, addressnowKey, isLoading }: AddressLookupProps) => {
    useEffect(() => {
        if (!addressnowKey && !isLoading) {
            loadPageByType("AffinitySettings");
        }
    }, [addressnowKey, isLoading, loadPageByType]);

    const formHandler: React.FormEventHandler<HTMLFormElement> = event => {
        event.preventDefault();
        const form = event.currentTarget;
        const formData = new FormData(form);
        const data = ObjectFromEntries(formData);
        const submitData: AddressType = {
            postcode: "",
            address1: "",
            address2: "",
            address3: "",
            towncity: "",
            county: "",
            name: "",
            ...data,
            rdcoUsageType: Number(data.rdcoUsageType) ?? 1, // 1-6 or 9, default 9? Need Commerce settings to define table of options and default value...
        }

        onSubmit(submitData);
    };

    return (
        <>
            <GlobalStyle />
            {addressnowKey && (
                <>
                    <Head
                        url={`//api.addressnow.co.uk/css/addressnow-2.20.min.css?key=key=${addressnowKey}`}
                        type="css"
                    />
                    <Head url={`//api.addressnow.co.uk/js/addressnow-2.20.min.js?key=${addressnowKey}`} type="js" />
                </>
            )}
            <StyledForm onSubmit={formHandler}>
                <TextField
                    label="Search for an address
                    "
                    placeholder="Start typing a postcode, street or address"
                    type="text"
                    id="address-lookup"
                    name="address-lookup"
                    autoComplete="off"
                />
                <TextField
                    label="Address 1"
                    required
                    type="text"
                    id="address1"
                    name="address1"
                    autoComplete="address-line-1"
                />
                <TextField
                    label="Address 2 (optional)"
                    type="text"
                    id="address2"
                    name="address2"
                    autoComplete="address-line-2"
                />
                <TextField
                    label="Address 3 (optional)"
                    type="text"
                    id="address3"
                    name="address3"
                    autoComplete="address-line-3"
                />
                <TextField label="Town/city" required type="text" id="towncity" name="towncity" />
                <TextField label="County" inputId="country" required type="text" id="county" name="county" />
                <TextField label="Postcode" inputId="postal-code" required type="text" id="postcode" name="postcode" />
                <Select
                    label="Please declare what you’ll be using fuel for at this address"
                    name="rdcoUsageType"
                    autoComplete="off"
                    hasLabel
                    required
                >
                    <option value={1}>Heating (non-domestic)</option>
                    <option value={2}>Other commercial use</option>
                    <option value={3}>Agricultural use</option>
                    <option value={4}>Transport</option>
                    <option value={5}>Local Authority</option>
                    <option value={6}>Auxiliary engines</option>
                    <option selected value={9}>Domestic</option>
                </Select>
                <TextField
                    label="Give this address an easy to remember name (optional)"
                    type="text"
                    id="name"
                    name="name"
                />
                <Divider />
                <Button
                    type="submit"
                    variant="primary"
                    sizeVariant="large"
                    icon={{ position: "right", src: "ChevronRight" }}
                    css={css`
                        align-self: end;
                    `}
                >
                    Add address
                </Button>
            </StyledForm>
        </>
    );
});

const StyledForm = styled.form`
    display: flex;
    flex-direction: column;
    gap: ${rem(20)};
    gap: ${rem(30)};
`;

const ObjectFromEntries = (iterable: any) => {
    return [...iterable].reduce((obj, [key, val]) => {
        obj[key] = val;
        return obj;
    }, {});
};
